'use client';
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { usePathname, useSearchParams } from 'next/navigation';
import { updateSearchParams } from '@overlapdev/functions';
import { AnimateWrapper } from '@overlapdev/wrappers';
import Link from 'next/link';
import { Button } from '@overlapdev/elements';
import Image from 'next/image';
import { LoadingSVG } from '@overlapdev/elements/assets';
import { ImageType, PageType, TaxonomyTermType } from '@overlapdev/types';
import { CircleArrowSVG, LoadMoreSVG, RestoreLogoSVG } from 'components/svg';

type PostType = PageType & { grid_image?: ImageType };

const BlogResults = ({ posts_per_page }: { posts_per_page: string }) => {
	//__Hooks
	const pathname = usePathname();
	const searchParams = useSearchParams();

	//__State
	const [category, setCategory] = useState(0);
	const [loaded, setLoaded] = useState(false);
	const [page, setPage] = useState(1);
	const [results, setResults] = useState<Array<PostType>>([]);
	const [total, setTotal] = useState(0);
	const [updating, setUpdating] = useState(false);

	//__Functions
	const update = async (offset: number, category: any, amount?: number) => {
		setUpdating(true);
		const query: any = {
			amount,
			category,
			offset,
			posts_per_page
		};
		const updated = await fetch(`/api/blog?query=${JSON.stringify(query)}`).then((res) => res.json());

		if (offset === 0) {
			//Reset results
			setResults(updated?.posts);
		} else {
			//Append new results to existing results
			if (updated?.posts?.length) {
				setResults((results) => [...results, ...updated?.posts]);
			}
		}
		setTotal(updated.total || 0);
		setUpdating(false);
	};

	const updatePage = () => {
		const new_page = page + 1;
		if (new_page.toString() !== searchParams.get('p')) {
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), 'p', new_page === 1 ? '' : new_page)}`);
		}
	};

	//__Use Effect
	useEffect(() => {
		const page_param = searchParams.has('p') ? parseInt(searchParams.get('p') || '', 10) : 1;
		const category_param = searchParams.has('cat') ? parseInt(searchParams.get('cat') || '', 10) : 0;

		if (!loaded) {
			const amount = page_param * parseInt(posts_per_page, 10);
			update(0, category_param, amount);
		} else {
			//If search or filters or date are changed
			if (category_param !== category) {
				update(0, category_param);
			} else if (page_param !== page) {
				const offset = (page_param - 1) * parseInt(posts_per_page, 10);
				update(offset, category_param);
			}
		}

		setCategory(category_param);
		setPage(page_param);
		setLoaded(true);
	}, [searchParams]);

	return (
		<div className='relative'>
			<div className='font-semibold mb-4 text-right'>{total} Results</div>
			{updating ? (
				<div className='absolute flex justify-center top-0 w-full'>
					<LoadingSVG width={200} fill='#373737' />
				</div>
			) : null}
			{results?.length ? (
				<div className='gap-5 grid grid-cols-1 md:grid-cols-2 xl:gap-10 lg:grid-cols-3'>
					{results.map((item: PostType) => {
						const { acf, id, post_title, relative_path } = item || {};
						const { categories, excerpt, grid_image } = acf || {};

						return (
							<AnimateWrapper key={`blog-${id}`} animate_type='fadeup' element='div' className='flex flex-col'>
								<div className='flex flex-col group grow overflow-hidden relative rounded-3xl transition-ease hover:shadow-[30px_35px_55px_0px_rgba(46,46,46,0.23)] hover:text-black hover:z-10'>
									<div className='h-[220px] overflow-hidden relative sm:h-[320px] md:h-[280px]'>
										{grid_image?.url ? (
											<Image src={grid_image.url} alt={grid_image.alt} fill className='object-cover object-center transition-ease sm:object-[50%_20%] group-hover:scale-110' sizes='640px' />
										) : (
											<div className='bg-black/10 flex h-full items-center justify-center p-4 lg:p-10'>
												<RestoreLogoSVG className='opacity-30' fill='#373737' />
											</div>
										)}
									</div>
									<div className='bg-white border-b border-x border-black/10 flex flex-col gap-4 grow p-8 rounded-b-3xl lg:p-10'>
										{categories?.length ? (
											<div className='flex flex-wrap gap-1'>
												{categories?.map((category_item: TaxonomyTermType, category_index: number) => (
													<div key={`blog_type_${category_item.term_id}`} className=''>
														{parse(`${category_item.name}${category_index + 1 < categories.length ? ' &nbsp;| ' : ''}`)}
													</div>
												))}
											</div>
										) : null}
										<h6 className='text-black'>{parse(post_title)}</h6>
										<div className='grow'>{excerpt ? <div className=''>{parse(excerpt)}</div> : null}</div>
										<Button link={{ url: relative_path, title: 'Read More' }} className='arrow_link group_hover'>
											<CircleArrowSVG width={18} />
										</Button>
									</div>
									{relative_path ? (
										<Link href={relative_path} className='absolute h-full left-0 top-0 w-full'>
											<span className='hidden'>{post_title}</span>
										</Link>
									) : null}
								</div>
							</AnimateWrapper>
						);
					})}
				</div>
			) : null}
			{results?.length < total ? (
				<div className='mt-10 text-center md:mt-16'>
					<Button className='arrow_link cursor-pointer no_animate' link={{ title: 'Load More', url: '' }} onClick={() => updatePage()}>
						<LoadMoreSVG className='text-black' width={20} />
					</Button>
				</div>
			) : null}
		</div>
	);
};

export default BlogResults;
